import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Party, PartyType, CreatePartyInput, PartyTransaction } from "@/types/party";
import {
  apiGetParties,
  apiGetPartyById,
  apiCreateParty,
  apiUpdateParty,
  apiArchiveParty,
  apiRestoreParty,
} from "@/lib/api/parties";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";

interface PartyContextType {
  parties: Party[];
  loading: boolean;
  refreshParties: () => Promise<void>;
  createParty: (input: CreatePartyInput) => Promise<{ party: Party; selectedLedgerId: string }>;
  updateParty: (id: string, input: Partial<CreatePartyInput>) => Promise<Party>;
  archiveParty: (id: string) => Promise<void>;
  restoreParty: (id: string) => Promise<void>;
  getParty: (id: string) => Party | undefined;
  getPartyById: (id: string) => Promise<{ party: Party; transactions: PartyTransaction[] }>;
  getPartiesByType: (type: PartyType | "all") => Party[];
}

const PartyContext = createContext<PartyContextType | null>(null);

export function PartyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { activeCompany } = useCompany();
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);

  const loadParties = useCallback(async () => {
    if (!isAuthenticated || !activeCompany?.id) {
      setParties([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await apiGetParties({
        companyId: activeCompany.id,
        status: "all",
      });
      setParties(res.parties || []);
    } catch (err) {
      console.error("[PartyContext] Failed to load parties", err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, activeCompany?.id]);

  useEffect(() => {
    loadParties();
  }, [loadParties]);

  const handleCreateParty = useCallback(
    async (input: CreatePartyInput) => {
      const res = await apiCreateParty({
        ...input,
        companyId: activeCompany?.id,
      });

      setParties((prev) => [res.party, ...prev]);

      // Trigger accounting context to pick up new ledger
      window.dispatchEvent(new CustomEvent("accounting:refresh-ledgers"));
      window.dispatchEvent(new CustomEvent("parties:updated"));

      return res;
    },
    [activeCompany?.id]
  );

  const handleUpdateParty = useCallback(
    async (id: string, input: Partial<CreatePartyInput>) => {
      const res = await apiUpdateParty(id, input);

      setParties((prev) => prev.map((p) => (p.id === id ? res.party : p)));

      window.dispatchEvent(new CustomEvent("accounting:refresh-ledgers"));
      window.dispatchEvent(new CustomEvent("parties:updated"));

      return res.party;
    },
    []
  );

  const handleArchiveParty = useCallback(async (id: string) => {
    await apiArchiveParty(id);
    setParties((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: "archived" as const } : p))
    );
    window.dispatchEvent(new CustomEvent("parties:updated"));
  }, []);

  const handleRestoreParty = useCallback(async (id: string) => {
    await apiRestoreParty(id);
    setParties((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: "active" as const } : p))
    );
    window.dispatchEvent(new CustomEvent("parties:updated"));
  }, []);

  const getParty = useCallback(
    (id: string) => parties.find((p) => p.id === id),
    [parties]
  );

  const getPartyById = useCallback(async (id: string) => {
    return await apiGetPartyById(id);
  }, []);

  const getPartiesByType = useCallback(
    (type: PartyType | "all") => {
      if (type === "all") return parties;
      if (type === "both") return parties.filter((p) => p.partyType === "both");
      return parties.filter((p) => p.partyType === type || p.partyType === "both");
    },
    [parties]
  );

  return (
    <PartyContext.Provider
      value={{
        parties,
        loading,
        refreshParties: loadParties,
        createParty: handleCreateParty,
        updateParty: handleUpdateParty,
        archiveParty: handleArchiveParty,
        restoreParty: handleRestoreParty,
        getParty,
        getPartyById,
        getPartiesByType,
      }}
    >
      {children}
    </PartyContext.Provider>
  );
}

export function useParties() {
  const ctx = useContext(PartyContext);
  if (!ctx) throw new Error("useParties must be used within PartyProvider");
  return ctx;
}
